import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MusicNotesSimple, Play, Pause, SkipForward, Warning, Shuffle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function BusinessPlayer() {
  const { commerceId } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/business/play/${commerceId}`);
        if (!mounted) return;
        const shuffled = shuffle(data.tracks || []);
        setQueue(shuffled);
        setIdx(0);
        setState({ loading: false, error: null, data });
      } catch (e) {
        if (!mounted) return;
        setState({
          loading: false,
          error: e?.response?.data?.detail || "No se pudo cargar el reproductor",
          status: e?.response?.status,
          data: null,
        });
      }
    })();
    return () => { mounted = false; };
  }, [commerceId]);

  const current = queue[idx];

  const next = useCallback(() => {
    setIdx(i => (i + 1 >= queue.length ? 0 : i + 1));
  }, [queue.length]);

  // Auto-advance on track end
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnded = () => next();
    a.addEventListener("ended", onEnded);
    return () => a.removeEventListener("ended", onEnded);
  }, [next]);

  // When index changes, load + play
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    a.src = current.audio_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${current.audio_url}` : current.audio_url;
    if (playing) {
      a.play().catch(() => setPlaying(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, queue.length]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
  };

  const reshuffle = () => {
    const s = shuffle(queue);
    setQueue(s); setIdx(0);
  };

  if (state.loading) {
    return <Container><p className="text-white/40">Cargando reproductor…</p></Container>;
  }

  if (state.error) {
    return (
      <Container>
        <div className="rounded-2xl bg-[#FF4B00]/5 border border-[#FF4B00]/20 p-8 max-w-md text-center" data-testid="biz-player-error">
          <Warning weight="fill" size={36} className="text-[#FF4B00] mx-auto mb-4" />
          <h2 className="font-display text-xl font-bold mb-2">No se puede reproducir</h2>
          <p className="text-white/70 text-sm mb-4">{state.error}</p>
          {state.status === 402 && (
            <p className="text-xs text-white/50">Renueva tu suscripción desde tu panel de negocio.</p>
          )}
          {state.status === 403 && (
            <p className="text-xs text-white/50">Este número está bloqueado para otra red WiFi. Contáctanos por WhatsApp para liberar el acceso si cambiaste de ubicación.</p>
          )}
        </div>
      </Container>
    );
  }

  const validUntil = state.data?.subscription_until ? new Date(state.data.subscription_until).toLocaleDateString("es-MX", { day: "2-digit", month: "long" }) : null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#FFB800] flex items-center justify-center">
              <MusicNotesSimple weight="fill" size={18} className="text-black" />
            </div>
            <div className="leading-tight">
              <p className="text-sm font-display font-bold" data-testid="biz-player-name">{state.data?.business?.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#B4F34A]">{commerceId}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Vigente</p>
            <p className="text-xs text-white/70 font-semibold">hasta {validUntil}</p>
          </div>
        </div>
      </header>

      {queue.length === 0 ? (
        <Container>
          <p className="text-white/40 text-center">Catálogo vacío por ahora. Vuelve más tarde.</p>
        </Container>
      ) : (
        <>
          {/* NOW PLAYING */}
          <main className="max-w-4xl mx-auto px-4 py-8">
            <div className="rounded-3xl bg-gradient-to-br from-[#121212] to-[#0a0a0a] border border-white/5 p-6 sm:p-10 mb-8" data-testid="biz-player-now">
              <div className="flex flex-col sm:flex-row gap-6 items-center">
                <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-2xl overflow-hidden shrink-0 bg-[#222]">
                  {current?.cover_url ? (
                    <img src={current.cover_url} alt={current?.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20"><MusicNotesSimple size={64} /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <p className="text-xs uppercase tracking-widest text-[#FFB800] mb-2">Sonando ahora</p>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold mb-1 truncate">{current?.title}</h2>
                  <p className="text-white/60 text-sm sm:text-base">{current?.artist_name}</p>
                  <p className="text-xs text-white/40 mt-1 capitalize">{(current?.genre || "").replace(/_/g, " ")}</p>

                  {/* Controls */}
                  <div className="mt-6 flex items-center justify-center sm:justify-start gap-3">
                    <button
                      onClick={togglePlay}
                      className="w-16 h-16 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black flex items-center justify-center active:scale-95 transition-all"
                      data-testid="biz-player-toggle"
                    >
                      {playing ? <Pause weight="fill" size={28} /> : <Play weight="fill" size={28} />}
                    </button>
                    <button
                      onClick={next}
                      className="w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                      data-testid="biz-player-next"
                      title="Siguiente"
                    >
                      <SkipForward weight="fill" size={20} />
                    </button>
                    <button
                      onClick={reshuffle}
                      className="w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                      data-testid="biz-player-shuffle"
                      title="Mezclar de nuevo"
                    >
                      <Shuffle weight="bold" size={18} />
                    </button>
                  </div>
                </div>
              </div>
              <audio ref={audioRef} preload="auto" />
            </div>

            {/* QUEUE */}
            <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Siguiente en la cola</p>
            <div className="space-y-1 max-h-96 overflow-y-auto pr-1" data-testid="biz-player-queue">
              {queue.slice(idx + 1, idx + 21).map((t, i) => (
                <button
                  key={t.id + i}
                  onClick={() => { setIdx(idx + 1 + i); setPlaying(true); }}
                  className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-[#222]">
                    {t.cover_url ? <img src={t.cover_url} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{t.title}</p>
                    <p className="text-xs text-white/40 truncate">{t.artist_name} · <span className="capitalize">{(t.genre || "").replace(/_/g, " ")}</span></p>
                  </div>
                </button>
              ))}
            </div>

            <p className="text-center text-xs text-white/30 mt-8">
              {queue.length} canciones · Reproducción aleatoria · Sin derechos de autor de terceros
            </p>
          </main>
        </>
      )}
    </div>
  );
}

function Container({ children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      {children}
    </div>
  );
}
